import React from 'react';
import {useIntl} from 'react-intl';
import Button from 'src/components/button';
import {errorMessageId, isInactiveCentralError} from './errors';
import InactiveCentralNotice from './inactive-central-notice';
import styles from './billing-block.module.scss';

export interface QueryLike {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

export const Truncated = ({text}: {text: string}) => (
  // Focusable so keyboard users reach the full text through the title, as mouse users do on hover.
  <span className={styles.truncate} title={text} tabIndex={0}>{text}</span>
);

export const RefreshButton = ({query}: {query: Pick<QueryLike, 'isFetching' | 'refetch'>}) => {
  const intl = useIntl();
  return (
    <Button className="neutral" color="secondary" size="sm" disabled={query.isFetching} onClick={() => query.refetch()}>
      {intl.formatMessage({id: 'billingBlockRefresh'})}
    </Button>
  );
};

interface SubtabHeaderProps {
  title: string;
  query: Pick<QueryLike, 'isFetching' | 'refetch'>;
  children?: React.ReactNode;
}

// Only for views whose title says something the subtab name does not.
export const SubtabHeader = ({title, query, children}: SubtabHeaderProps) => (
  <div className={styles.header}>
    <h3 className={styles.title}>{title}</h3>
    <div className="d-flex flex-wrap align-items-center">
      {children}
      <RefreshButton query={query} />
    </div>
  </div>
);

interface QueryStateProps {
  query: QueryLike;
  children: () => React.ReactNode;
}

export const QueryState = ({query, children}: QueryStateProps) => {
  const intl = useIntl();
  if (query.isError && isInactiveCentralError(query.error)) return <InactiveCentralNotice />;
  // A failed query keeps status "error" while it retries, so the loading check has to include it.
  if (query.isLoading || (query.isError && query.isFetching)) {
    return <div role="status" className={styles.placeholder}>{intl.formatMessage({id: 'billingBlockLoading'})}</div>;
  }
  if (query.isError) {
    return (
      <div role="alert" className={styles.placeholder}>
        <p>{intl.formatMessage({id: errorMessageId(query.error)})}</p>
        <Button className="neutral" color="secondary" size="sm" onClick={() => query.refetch()}>
          {intl.formatMessage({id: 'billingBlockRetry'})}
        </Button>
      </div>
    );
  }
  return <>{children()}</>;
};

interface ListStateProps<T> {
  query: QueryLike;
  rows: T[];
  emptyMessage: string;
  emptyAction?: {label: string; onClick: () => void};
  // Shown in the table's own toolbar by the caller; repeated here when there is no table to hold them.
  actions?: React.ReactNode;
  children: (rows: T[]) => React.ReactNode;
}

export const ListState = <T, >({query, rows, emptyMessage, emptyAction, actions, children}: ListStateProps<T>) => {
  const hasTable = !query.isLoading && !query.isError && rows.length > 0;
  const inactive = query.isError && isInactiveCentralError(query.error);
  return (
    <>
      {!hasTable && !inactive && actions && <div className={styles.toolbar}>{actions}</div>}
      <QueryState query={query}>
        {() => (rows.length === 0 ? (
          <div className={styles.placeholder} data-empty>
            <p>{emptyMessage}</p>
            {emptyAction && (
              <Button className="neutral" color="secondary" size="sm" onClick={emptyAction.onClick}>{emptyAction.label}</Button>
            )}
          </div>
        ) : children(rows))}
      </QueryState>
    </>
  );
};
